import type { Session, SupabaseClient } from '@supabase/supabase-js';
import type {
  AccountType,
  Activity,
  ActivityEvent,
  AppState,
  AreaType,
  Attachment,
  Estate,
  Field,
  Plan,
  Product,
  SubscriptionTier,
} from '../types';
import {
  inferAreaTypeFromName,
  isZoneField,
  isNatureOrZoneField,
  AREA_TYPE_MAP,
  DEFAULT_PRODUCTS,
} from './domain';
import { captureLegacyPlans } from './plans';
import { getAttachment, validateState } from './storage';
import { supabase } from './supabase';

export { configured, cloudStatus, supabase } from './supabase';
export type { SubscriptionTier } from '../types';
export type MembershipRole = 'owner' | 'advisor' | 'viewer';
export interface Membership {
  estateId: string;
  userId: string;
  role: MembershipRole;
}
export interface EstateMember extends Membership {
  name: string;
  email: string;
}
export interface Invitation {
  id: string;
  estateId: string;
  email: string;
  role: MembershipRole;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}
const attachmentBucket = 'estate-attachments';
const mimeTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
let baseUserId = '';
let identityId: string | null | undefined;
let identityVersion = 0;
const stateIdentities = new WeakMap<AppState, string | null>();
const estateRevisions = new Map<string, number>();
const estateRoles = new Map<string, MembershipRole>();
const cloudAttachmentIds = new Set<string>();
let saving: Promise<unknown> = Promise.resolve();
let cloudReadVersion = 0;

export const isCloudAttachment = (id: string): boolean => cloudAttachmentIds.has(id);
export const getEstateRole = (estateId: string): MembershipRole | null =>
  estateRoles.get(estateId) ?? null;
export function invalidateCloudBaseline(): void {
  estateRevisions.clear();
  estateRoles.clear();
  cloudAttachmentIds.clear();
}
export function setCloudIdentity(userId: string | null): void {
  if (identityId === userId) return;
  identityId = userId;
  identityVersion += 1;
  baseUserId = '';
  invalidateCloudBaseline();
}
export function bindWorkspaceState(state: AppState, userId: string | null): void {
  stateIdentities.set(state, userId);
}

function assertStateOwner(state: AppState, userId: string): void {
  const owner = stateIdentities.get(state);
  if (owner !== undefined && owner !== null && owner !== userId) throw sessionChanged();
}

function activityFromRow(row: Record<string, any>): Activity {
  return {
    id: row.id,
    estateId: row.estate_id,
    fieldId: row.field_id ?? undefined,
    kind: row.kind,
    date: row.date,
    status: row.status,
    description: row.description,
    quantity: row.quantity == null ? undefined : Number(row.quantity),
    unit: row.unit ?? undefined,
    createdAt: row.created_at,
  };
}

interface SessionGuard {
  id: string;
  sessionEpoch: number;
}
const sessionChanged = () =>
  new Error(
    'Kontoen skiftede under handlingen. Svaret er afvist; tidligere ændringer kan være nået til serveren.',
  );
async function assertSession(guard: SessionGuard): Promise<void> {
  if (identityVersion !== guard.sessionEpoch) throw sessionChanged();
  const session = await getSession();
  if (session?.user.id !== guard.id || identityVersion !== guard.sessionEpoch)
    throw sessionChanged();
}

async function rows(
  table: string,
  columns: string,
  orderColumn = 'id',
  options?: { countExact?: boolean },
) {
  const collected = [];
  for (let offset = 0; ; offset += 500) {
    let query = client()
      .from(table)
      .select(columns, options?.countExact ? { count: 'exact' } : undefined)
      .order(orderColumn)
      .range(offset, offset + 499);
    if (table === 'estate_memberships') query = query.order('user_id');
    // Dynamic table/column selection is checked after conversion by validateState.
    const { data, error, count } = await query.overrideTypes<
      Record<string, any>[],
      { merge: false }
    >();
    fail(error, `${table} kunne ikke hentes`);
    collected.push(...(data ?? []));
    if ((data?.length ?? 0) < 500) {
      if (count != null && collected.length < count)
        throw new Error(`${table} blev afkortet af serveren. Ingen data er erstattet.`);
      return collected;
    }
    if (collected.length >= 100_000)
      throw new Error(
        `${table} indeholder for mange poster. Hentningen blev afbrudt; ingen data er erstattet.`,
      );
  }
}

export function client(): SupabaseClient {
  if (!supabase) throw new Error('Cloud er ikke konfigureret. Dine lokale data er bevaret.');
  return supabase;
}

function fail(error: { message: string } | null, operation: string): void {
  if (error) throw new Error(`${operation}: ${error.message}`);
}

async function authenticatedUser() {
  const startVersion = identityVersion;
  const session = await getSession();
  if (identityVersion !== startVersion) throw sessionChanged();
  if (!session?.user) throw new Error('Log ind for at bruge cloud. Dine lokale data er bevaret.');
  setCloudIdentity(session.user.id);
  const sessionEpoch = identityVersion;
  const { data, error } = await client().auth.getUser();
  fail(error, 'Login kunne ikke kontrolleres');
  if (!data.user) throw new Error('Log ind for at bruge cloud. Dine lokale data er bevaret.');
  if (data.user.id !== session.user.id) throw sessionChanged();
  await assertSession({ id: data.user.id, sessionEpoch });
  if (baseUserId !== data.user.id) {
    estateRevisions.clear();
    estateRoles.clear();
    cloudAttachmentIds.clear();
    baseUserId = data.user.id;
  }
  return { ...data.user, sessionEpoch };
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await client().auth.getSession();
  fail(error, 'Sessionen kunne ikke læses');
  return data.session;
}

export async function signIn(email: string, password: string): Promise<void> {
  const normalized = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized))
    throw new Error('Indtast en gyldig e-mailadresse.');
  if (!password) throw new Error('Indtast din adgangskode.');
  const { error } = await client().auth.signInWithPassword({ email: normalized, password });
  if (error) throw new Error('Forkert e-mail eller adgangskode.');
}

export async function signUp(
  email: string,
  password: string,
  name?: string,
): Promise<{ needsConfirmation?: boolean }> {
  const normalized = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized))
    throw new Error('Indtast en gyldig e-mailadresse.');
  if (!password || password.length < 6) throw new Error('Adgangskoden skal have mindst 6 tegn.');
  const { data, error } = await client().auth.signUp({
    email: normalized,
    password,
    options: {
      data: name ? { name: name.trim() } : undefined,
    },
  });
  if (error) throw new Error(error.message || 'Profilen kunne ikke oprettes.');
  // If email confirmation is disabled or session is directly returned
  const needsConfirmation = !data.session;
  return { needsConfirmation };
}

export async function signOut(): Promise<void> {
  const { error } = await client().auth.signOut();
  fail(error, 'Du kunne ikke logges ud');
  if ((await getSession())?.user) throw sessionChanged();
  setCloudIdentity(null);
}

export async function listMemberships(): Promise<Membership[]> {
  const user = await authenticatedUser();
  const data = await rows('estate_memberships', 'estate_id,user_id,role', 'estate_id');
  const memberships = data.map((row) => ({
    estateId: row.estate_id,
    userId: row.user_id,
    role: row.role as MembershipRole,
  }));
  await assertSession(user);
  estateRoles.clear();
  for (const membership of memberships)
    if (membership.userId === user.id) estateRoles.set(membership.estateId, membership.role);
  return memberships;
}

export async function listEstateMembers(estateId: string): Promise<EstateMember[]> {
  const user = await authenticatedUser();
  const { data, error } = await client().rpc('list_estate_members', { estate_key: estateId });
  fail(error, 'Ejendommens medlemmer kunne ikke hentes');
  await assertSession(user);
  if (!Array.isArray(data)) throw new Error('Serveren returnerede en ugyldig medlemsoversigt.');
  return data.map((row) => ({
    estateId: row.estate_id,
    userId: row.user_id,
    role: row.role,
    name: row.name ?? '',
    email: row.email ?? '',
  }));
}

export function loadCloudState(fallback: AppState): Promise<AppState> {
  const next = saving.then(
    () => readCloudState(fallback),
    () => readCloudState(fallback),
  );
  saving = next;
  return next;
}
async function readCloudState(fallback: AppState): Promise<AppState> {
  const user = await authenticatedUser();
  assertStateOwner(fallback, user.id);
  assertStateOwner(fallback, user.id);
  const connection = client();
  const estateRows = await rows(
    'estates',
    'id,owner_user_id,name,address,center,created_at,revision',
  );
  const results = await Promise.all([
    rows('fields', 'id,estate_id,name,geometry,notes,updated_at'),
    rows('plans', 'id,estate_id,name,year,status,notes,selections,updated_at,snapshot'),
    rows(
      'activities',
      'id,estate_id,field_id,kind,date,status,description,quantity,unit,created_at',
    ),
    rows('attachments', 'id,estate_id,field_id,name,mime,size,created_at'),
    rows(
      'activity_history',
      'id,estate_id,activity_id,action,at,actor_user_id,before_data,after_data',
    ),
    rows(
      'products',
      'id,name,rate_kg_ha,pack_kg,price_dkk,description,color,url,active,verified,variant_id,source,checked_at,unit',
    ),
    connection
      .from('profiles')
      .select(
        'name,company,terms_accepted_at,terms_version,analytics_consent,marketing_consent,marketing_consent_at',
      )
      .eq('user_id', user.id)
      .maybeSingle(),
  ]);
  const [fieldRows, planRows, activityRows, attachmentRows, historyRows, productRows, profileRow] =
    results;
  fail(profileRow.error, 'Profilen kunne ikke hentes');
  const estates: Estate[] = estateRows.map((row) => ({
    id: row.id,
    name: row.name || 'Mit Revir',
    address: row.address || '',
    center: (Array.isArray(row.center) &&
    row.center.length === 2 &&
    row.center.every((n: any) => typeof n === 'number' && Number.isFinite(n))
      ? [Number(row.center[0]), Number(row.center[1])]
      : [11.82, 55.4]) as [number, number],
    createdAt: row.created_at || (row as any).createdAt || new Date().toISOString(),
    ownerUserId: row.owner_user_id,
  }));
  const fields: Field[] = fieldRows.map((row) => {
    const rawNotes = row.notes || '';
    const isAdvisorOnly =
      rawNotes.includes('[RÅDGIVER_KLADDE]') ||
      (row as any).advisorOnly === true ||
      (row as any).advisor_only === true;
    let cleanNotes = rawNotes.replace(/\[RÅDGIVER_KLADDE\]\s*/g, '');
    const match = cleanNotes.match(/^\[OMRÅDE(?::([a-z_]+))?\]\s*/);
    const rowType = ((row as any).areaType || (row as any).area_type) as AreaType | undefined;
    let areaType: AreaType = 'mark';
    let kind: 'field' | 'zone' = 'field';

    if (rowType && AREA_TYPE_MAP[rowType]) {
      areaType = rowType;
      kind = rowType === 'jagtomraade' ? 'zone' : 'field';
    } else if (match) {
      const typeId = (match[1] as AreaType) || 'jagtomraade';
      areaType = typeId;
      kind = typeId === 'jagtomraade' ? 'zone' : 'field';
      cleanNotes = cleanNotes.slice(match[0].length);
    } else if (cleanNotes.startsWith('[POI:')) {
      areaType = 'andet';
      kind = 'zone';
    } else {
      const inferred = inferAreaTypeFromName(row.name);
      if (inferred) {
        areaType = inferred;
        kind = inferred === 'jagtomraade' ? 'zone' : 'field';
      }
    }

    if (
      areaType === 'remise' &&
      (inferAreaTypeFromName(row.name || '') === 'laehegn' || cleanNotes.includes('Plantet læhegn'))
    ) {
      areaType = 'laehegn';
      kind = 'field';
    }

    return {
      id: row.id,
      estateId: row.estate_id || (row as any).estateId || '',
      name: row.name,
      geometry: row.geometry,
      notes: cleanNotes,
      kind,
      areaType,
      ...(isAdvisorOnly ? { advisorOnly: true } : {}),
      updatedAt: row.updated_at || (row as any).updatedAt || new Date().toISOString(),
    };
  });
  const plans: Plan[] = planRows.map((row) => {
    const validFieldIds = new Set(
      fields.filter((f) => f.estateId === row.estate_id).map((f) => f.id),
    );
    const cleanedSelections: Record<string, string> = {};
    if (row.selections && typeof row.selections === 'object') {
      for (const [fId, prodId] of Object.entries(row.selections)) {
        if (validFieldIds.has(fId) && typeof prodId === 'string') {
          cleanedSelections[fId] = prodId;
        }
      }
    }
    return {
      id: row.id,
      estateId: row.estate_id || (row as any).estateId || '',
      name: row.name,
      year: row.year,
      status: row.status,
      notes: row.notes,
      selections: cleanedSelections,
      updatedAt: row.updated_at || (row as any).updatedAt || new Date().toISOString(),
      snapshot: row.snapshot ?? undefined,
    };
  });
  // Ensure every estate has at least one active plan
  for (const estate of estates) {
    if (!plans.some((p) => p.estateId === estate.id)) {
      const year = new Date().getFullYear();
      plans.push({
        id: `plan-${estate.id.replace(/^estate-/, '')}-${year}`,
        estateId: estate.id,
        name: `Plejeplan ${year}`,
        year,
        status: 'draft',
        notes: '',
        selections: {},
        updatedAt: new Date().toISOString(),
      });
    }
  }
  const activities: Activity[] = activityRows.map(activityFromRow);
  const serverHistory: ActivityEvent[] = historyRows.map((row) => ({
    id: row.id,
    estateId: row.estate_id,
    activityId: row.activity_id,
    action: row.action,
    at: row.at,
    actor: row.actor_user_id || 'System',
    source: 'cloud',
    before: row.before_data ? activityFromRow(row.before_data) : undefined,
    after: row.after_data ? activityFromRow(row.after_data) : undefined,
  }));
  const estateIds = new Set(estates.map((estate) => estate.id));
  const remoteEventIds = new Set(serverHistory.map((event) => event.id));
  const localHistory = (fallback.activityHistory ?? []).filter(
    (event) =>
      event.source !== 'cloud' && estateIds.has(event.estateId) && !remoteEventIds.has(event.id),
  );
  const remoteAttachments: Attachment[] = attachmentRows.map((row) => ({
    id: row.id,
    estateId: row.estate_id,
    fieldId: row.field_id ?? undefined,
    name: row.name,
    mime: row.mime,
    size: Number(row.size),
    createdAt: row.created_at,
    storage: 'cloud',
  }));
  const remoteAttachmentIds = new Set(remoteAttachments.map((item) => item.id));
  const fieldIds = new Set(fields.map((item) => item.id));
  const attachments = [
    ...remoteAttachments,
    ...fallback.attachments.filter(
      (item) =>
        item.storage !== 'cloud' &&
        estateIds.has(item.estateId) &&
        (!item.fieldId || fieldIds.has(item.fieldId)) &&
        !remoteAttachmentIds.has(item.id),
    ),
  ];
  const products: Product[] = productRows.map((row) => ({
    id: row.id,
    name: row.name,
    rateKgHa: Number(row.rate_kg_ha),
    packKg: Number(row.pack_kg),
    unit: row.unit ?? 'kg',
    priceDkk: Number(row.price_dkk),
    description: row.description,
    color: row.color,
    url: row.url,
    active: row.active,
    verified: row.verified,
    variantId: row.variant_id ?? undefined,
    source: row.source ?? undefined,
    checkedAt: row.checked_at ?? undefined,
  }));
  // An empty server catalog is a clearly unverified demo catalog, never sales approval.
  if (!products.length) {
    const catalogBase = fallback.products?.length ? fallback.products : DEFAULT_PRODUCTS;
    products.push(...catalogBase.map((product) => ({ ...product, verified: false })));
  } else {
    const referenced = new Set(plans.flatMap((plan) => Object.values(plan.selections)));
    const fallbackList = fallback.products?.length ? fallback.products : DEFAULT_PRODUCTS;
    for (const product of fallbackList) {
      if (referenced.has(product.id) && !products.some((item) => item.id === product.id))
        products.push({ ...product, active: false, verified: false });
    }
  }
  // Sikr at alle refererede produkter i planer altid findes i kataloget
  const allReferenced = new Set(plans.flatMap((plan) => Object.values(plan.selections)));
  for (const prodId of allReferenced) {
    if (!products.some((p) => p.id === prodId)) {
      const match = DEFAULT_PRODUCTS.find((p) => p.id === prodId);
      if (match) {
        products.push({ ...match, active: false, verified: false });
      } else {
        products.push({
          id: prodId,
          name: prodId,
          description: 'Historisk frøblanding',
          rateKgHa: 10,
          packKg: 10,
          priceDkk: 100,
          color: '#64748b',
          url: '',
          active: false,
          verified: false,
          unit: 'kg',
        });
      }
    }
  }
  const ownEstates = user?.id
    ? estates.filter((estate) => !estate.ownerUserId || estate.ownerUserId === user.id)
    : estates;
  const selectedEstateId = ownEstates.some((estate) => estate.id === fallback.selectedEstateId)
    ? fallback.selectedEstateId
    : (ownEstates[0]?.id ?? '');
  const estatePlans = plans.filter((plan) => plan.estateId === selectedEstateId);
  const state: AppState = {
    version: 1,
    estates,
    fields,
    plans,
    activities,
    tasks: fallback.tasks || [],
    activityHistory: [...localHistory, ...serverHistory],
    attachments,
    products,
    profile: {
      name: profileRow.data?.name ?? '',
      company: profileRow.data?.company ?? '',
      email: user.email ?? '',
      terms_accepted_at: profileRow.data?.terms_accepted_at ?? null,
      terms_version: profileRow.data?.terms_version ?? 'v1.0',
      analytics_consent: profileRow.data?.analytics_consent ?? false,
      marketing_consent: profileRow.data?.marketing_consent ?? false,
      marketing_consent_at: profileRow.data?.marketing_consent_at ?? null,
    },
    selectedEstateId,
    selectedPlanId: estatePlans.some((plan) => plan.id === fallback.selectedPlanId)
      ? fallback.selectedPlanId
      : (estatePlans[0]?.id ?? ''),
  };
  if (!validateState(state))
    throw new Error(
      'Cloud-data er ugyldige eller bruger ukendte produkter. Lokale data er bevaret; kontakt administratoren.',
    );
  await listMemberships();
  await assertSession(user);
  const afterRead = await rows('estates', 'id,revision');
  if (
    afterRead.length !== estateRows.length ||
    afterRead.some(
      (estate) =>
        !estateRows.some(
          (before) => before.id === estate.id && before.revision === estate.revision,
        ),
    )
  ) {
    throw new Error(
      'En ejendom blev ændret, mens cloud blev hentet. Hent igen; lokale data er bevaret.',
    );
  }
  for (const estate of estateRows) {
    if (
      typeof estate.revision !== 'number' ||
      !Number.isSafeInteger(estate.revision) ||
      estate.revision < 0
    )
      throw new Error('Cloud returnerede en ugyldig versionsværdi; lokale data er bevaret.');
  }
  await assertSession(user);
  estateRevisions.clear();
  for (const estate of estateRows) estateRevisions.set(estate.id, Number(estate.revision));
  cloudAttachmentIds.clear();
  for (const attachment of remoteAttachments) cloudAttachmentIds.add(attachment.id);
  cloudReadVersion += 1;
  const captured = captureLegacyPlans(state);
  bindWorkspaceState(captured, user.id);
  return captured;
}

async function sync(
  state: AppState,
  removeMissing: boolean,
  expected: SessionGuard,
): Promise<void> {
  if (!validateState(state))
    throw new Error('Planen indeholder ugyldige data og kan ikke gemmes i cloud.');
  if (state.estates.some((estate) => estate.id.startsWith('example-')))
    throw new Error(
      'Fjern eksempelejendommen før overførsel til en konto. Eksportér lokale data først, hvis du vil beholde eksemplet.',
    );
  const user = await authenticatedUser();
  assertStateOwner(state, user.id);
  if (user.id !== expected.id || user.sessionEpoch !== expected.sessionEpoch)
    throw sessionChanged();
  if (state.estates.some((estate) => estateRoles.get(estate.id) === 'viewer'))
    throw new Error(
      'Du har kun læseadgang til en af ejendommene. Gem kun ejendomme, hvor du kan redigere.',
    );
  const baseRevisions = Object.fromEntries(
    state.estates.flatMap((estate) =>
      estateRevisions.has(estate.id) ? [[estate.id, estateRevisions.get(estate.id)]] : [],
    ),
  );
  const serializedSnapshot: AppState = {
    ...state,
    fields: state.fields.map((f) => {
      if (f.notes && f.notes.startsWith('[POI:')) {
        let poiNotes = f.notes;
        if (f.advisorOnly && !poiNotes.includes('[RÅDGIVER_KLADDE]')) {
          poiNotes = `[RÅDGIVER_KLADDE] ${poiNotes}`;
        }
        return {
          ...f,
          areaType: 'andet',
          kind: 'zone',
          notes: poiNotes,
        };
      }
      const isNatureOrZone = isNatureOrZoneField(f);
      const effectiveAreaType =
        f.areaType || (isNatureOrZone ? (f.kind === 'zone' ? 'jagtomraade' : 'andet') : 'mark');
      const clean = (f.notes || '')
        .replace(/^\[RÅDGIVER_KLADDE\]\s*/g, '')
        .replace(/^\[OMRÅDE(?::[a-z_]+)?\]\s*/, '');
      let baseNotes = '';
      if (effectiveAreaType !== 'mark') {
        baseNotes = clean
          ? `[OMRÅDE:${effectiveAreaType}] ${clean}`
          : `[OMRÅDE:${effectiveAreaType}]`;
      } else {
        baseNotes = inferAreaTypeFromName(f.name)
          ? clean
            ? `[OMRÅDE:mark] ${clean}`
            : '[OMRÅDE:mark]'
          : clean;
      }
      const finalNotes = f.advisorOnly ? `[RÅDGIVER_KLADDE] ${baseNotes}`.trim() : baseNotes;
      return {
        ...f,
        areaType: effectiveAreaType,
        notes: finalNotes,
      };
    }),
  };
  const { data, error } = await client().rpc('sync_plotwise_state', {
    snapshot: serializedSnapshot,
    remove_missing: removeMissing,
    base_revisions: baseRevisions,
  });
  fail(error, 'Planen blev ikke gemt i cloud; lokale data er bevaret');
  await assertSession(user);
  if (!data || typeof data !== 'object' || Array.isArray(data))
    throw new Error(
      'Planen er sendt, men serverens versionskvittering er ugyldig. Hent cloud før du gemmer igen.',
    );
  for (const estate of state.estates) {
    const revision = data[estate.id];
    if (typeof revision !== 'number' || !Number.isSafeInteger(revision) || revision < 0)
      throw new Error(
        'Planen er sendt, men serverens versionskvittering er ugyldig. Hent cloud før du gemmer igen.',
      );
    estateRevisions.set(estate.id, revision);
  }
}

// Save writable estates while leaving viewer data visible and unchanged locally.
// Attachments use uploadAttachment/removeAttachment; catalog writes are admin-only.
export function saveCloudState(state: AppState): Promise<void> {
  const readVersion = cloudReadVersion;
  const invocationEpoch = identityVersion;
  const run = () => {
    if (identityVersion !== invocationEpoch) throw sessionChanged();
    return saveCloudStateNow(state, readVersion);
  };
  // Reserve the entire operation at invocation time, before auth/member awaits.
  const next = saving.then(run, run);
  saving = next;
  return next;
}
async function saveCloudStateNow(state: AppState, readVersion: number): Promise<void> {
  if (!validateState(state))
    throw new Error('Planen indeholder ugyldige data og kan ikke gemmes i cloud.');
  const user = await authenticatedUser();
  assertStateOwner(state, user.id);
  await listMemberships();
  await assertSession(user);
  const estates = state.estates.filter((estate) => estateRoles.get(estate.id) !== 'viewer');
  const writableIds = new Set(estates.map((estate) => estate.id));
  const plans = state.plans.filter((plan) => writableIds.has(plan.estateId));
  const selectedEstateId = writableIds.has(state.selectedEstateId)
    ? state.selectedEstateId
    : (estates[0]?.id ?? '');
  const estatePlans = plans.filter((plan) => plan.estateId === selectedEstateId);
  const snapshot: AppState = {
    ...state,
    estates,
    fields: state.fields.filter((field) => writableIds.has(field.estateId)),
    plans,
    activities: state.activities.filter((activity) => writableIds.has(activity.estateId)),
    tasks: state.tasks.filter((task) => writableIds.has(task.estateId)),
    activityHistory: state.activityHistory?.filter((event) => writableIds.has(event.estateId)),
    attachments: state.attachments.filter((attachment) => writableIds.has(attachment.estateId)),
    selectedEstateId,
    selectedPlanId: estatePlans.some((plan) => plan.id === state.selectedPlanId)
      ? state.selectedPlanId
      : (estatePlans[0]?.id ?? ''),
  };
  if (readVersion !== cloudReadVersion)
    throw new Error(
      'Kontoen blev opdateret før gemningen. Lokale ændringer er bevaret og sammenlignes igen.',
    );
  await sync(snapshot, true, user);
}

export async function migrateLocalState(state: AppState): Promise<AppState> {
  const readVersion = cloudReadVersion;
  const invocationEpoch = identityVersion;
  // Reserve only the first preflight+sync operation. Subsequent reads/uploads run
  // after it settles, never nested within their own queue reservation.
  const run = async () => {
    if (identityVersion !== invocationEpoch) throw sessionChanged();
    const user = await authenticatedUser();
    if (readVersion !== cloudReadVersion)
      throw new Error(
        'Kontoen blev opdateret før overførslen. Lokale ændringer er bevaret; sammenlign versionerne før du prøver igen.',
      );
    await assertSession(user);
    await sync(state, false, user);
    return user;
  };
  const initialSync = saving.then(run, run);
  saving = initialSync;
  const user = await initialSync;
  const cloud = await loadCloudState(state);
  await assertSession(user);
  const failures: string[] = [];
  for (const attachment of state.attachments) {
    if (
      cloud.attachments.some(
        (item) => item.id === attachment.id && item.estateId === attachment.estateId,
      )
    )
      continue;
    try {
      await assertSession(user);
      const file = await getAttachment(attachment.id);
      if (!file) throw new Error('Filen findes ikke i denne browsers fillager.');
      if (file.type !== attachment.mime || file.size !== attachment.size)
        throw new Error('Filens størrelse eller format stemmer ikke med metadata.');
      await uploadFile(attachment, file);
      await assertSession(user);
    } catch (error) {
      failures.push(
        `${attachment.name}: ${error instanceof Error ? error.message : 'Ukendt fejl.'}`,
      );
    }
  }
  if (failures.length)
    throw new Error(
      `Marker og planer er overført, men ${failures.length} fil(er) mangler. Lokale data er bevaret. ${failures.join(' ')}`,
    );
  const result = await loadCloudState(state);
  await assertSession(user);
  return result;
}

export async function deleteEstate(estateId: string): Promise<void> {
  const user = await authenticatedUser();
  const memberships = await listMemberships();
  await assertSession(user);
  const isAdmin = user.app_metadata?.plotwise_admin === true;
  if (
    !isAdmin &&
    !memberships.some(
      (item) => item.estateId === estateId && item.userId === user.id && item.role === 'owner',
    )
  )
    throw new Error('Kun ejeren eller en administrator kan slette ejendommen.');
  const paths: string[] = [];
  try {
    for (let offset = 0; ; offset += 500) {
      const result = await client()
        .storage.from(attachmentBucket)
        .list(estateId, { limit: 500, offset, sortBy: { column: 'name', order: 'asc' } });
      await assertSession(user);
      if (result.error) break;
      paths.push(...(result.data ?? []).map((item) => `${estateId}/${item.name}`));
      if ((result.data?.length ?? 0) < 500) break;
      if (paths.length >= 100_000) break;
    }
  } catch {
    // Fortsæt til sletning af ejendommen selvom storage-opslag fejler
  }
  for (let index = 0; index < paths.length; index += 100) {
    const batch = paths.slice(index, index + 100);
    try {
      await assertSession(user);
      await client().storage.from(attachmentBucket).remove(batch);
    } catch {
      // Ignorer fejl i sletning af enkelte filer
    }
  }
  await assertSession(user);
  const { data, error } = await client().from('estates').delete().eq('id', estateId).select('id');
  await assertSession(user);
  fail(error, 'Ejendommen kunne ikke slettes; dens filer kan allerede være fjernet');
  if (!data?.length) throw new Error('Ejendommen findes ikke, eller du er ikke ejeren.');
  estateRevisions.delete(estateId);
  estateRoles.delete(estateId);
}

export async function createInvitation(
  estateId: string,
  email: string,
  role: MembershipRole,
): Promise<{ id: string; token: string; expiresAt: string }> {
  const guard = await authenticatedUser();
  const { data, error } = await client().rpc('create_estate_invitation', {
    estate_key: estateId,
    recipient_email: email,
    invited_role: role,
  });
  await assertSession(guard);
  fail(error, 'Invitationen kunne ikke oprettes');
  if (!data?.id || !data?.token || !data?.expiresAt)
    throw new Error('Serveren returnerede en ugyldig invitation.');
  return data;
}

export async function listInvitations(): Promise<Invitation[]> {
  const guard = await authenticatedUser();
  const data = await rows(
    'estate_invitations',
    'id,estate_id,email,role,expires_at,accepted_at,revoked_at',
  );
  await assertSession(guard);
  return data.map((row) => ({
    id: row.id,
    estateId: row.estate_id,
    email: row.email,
    role: row.role,
    expiresAt: row.expires_at,
    acceptedAt: row.accepted_at,
    revokedAt: row.revoked_at,
  }));
}

export async function acceptInvitation(token: string): Promise<string> {
  const guard = await authenticatedUser();
  const { data, error } = await client().rpc('accept_estate_invitation', {
    invitation_token: token,
  });
  await assertSession(guard);
  fail(error, 'Invitationen kunne ikke accepteres');
  if (typeof data !== 'string') throw new Error('Serveren returnerede en ugyldig ejendom.');
  return data;
}

export async function revokeInvitation(id: string): Promise<void> {
  const guard = await authenticatedUser();
  const { error } = await client().rpc('revoke_estate_invitation', { invitation_id: id });
  await assertSession(guard);
  fail(error, 'Invitationen kunne ikke tilbagekaldes');
}

export async function revokeMember(estateId: string, userId: string): Promise<void> {
  const guard = await authenticatedUser();
  const { error } = await client().rpc('revoke_estate_member', {
    estate_key: estateId,
    member_user_id: userId,
  });
  await assertSession(guard);
  fail(error, 'Adgangen kunne ikke fjernes');
}

const objectPath = (attachment: Attachment) => `${attachment.estateId}/${attachment.id}`;

async function uploadFile(attachment: Attachment, file: Blob): Promise<void> {
  const guard = await authenticatedUser();
  if (!mimeTypes.includes(file.type) || file.size > 20 * 1024 * 1024)
    throw new Error('Kun PDF, JPEG, PNG og WebP på højst 20 MB kan uploades.');
  const connection = client();
  const path = objectPath(attachment);
  const { error: uploadError } = await connection.storage
    .from(attachmentBucket)
    .upload(path, file, { contentType: file.type, upsert: true });
  await assertSession(guard);
  fail(uploadError, 'Filen kunne ikke uploades');
  const { error } = await connection.from('attachments').upsert({
    id: attachment.id,
    estate_id: attachment.estateId,
    field_id: attachment.fieldId ?? null,
    name: attachment.name,
    mime: file.type,
    size: file.size,
    created_at: attachment.createdAt,
  });
  await assertSession(guard);
  if (error) {
    const cleanup = await connection.storage.from(attachmentBucket).remove([path]);
    throw new Error(
      `Filens metadata blev ikke gemt: ${error.message}${cleanup.error ? `. Den uploadede fil kunne heller ikke ryddes op: ${cleanup.error.message}` : ''}`,
    );
  }
  cloudAttachmentIds.add(attachment.id);
}

export async function uploadAttachment(
  estateId: string,
  file: File,
  fieldId?: string,
): Promise<Attachment> {
  const attachment: Attachment = {
    id: crypto.randomUUID(),
    estateId,
    fieldId,
    name: file.name,
    mime: file.type,
    size: file.size,
    createdAt: new Date().toISOString(),
    storage: 'cloud',
  };
  await uploadFile(attachment, file);
  return attachment;
}

export async function downloadAttachment(attachment: Attachment): Promise<Blob> {
  const guard = await authenticatedUser();
  const { data, error } = await client()
    .storage.from(attachmentBucket)
    .download(objectPath(attachment));
  await assertSession(guard);
  fail(error, 'Filen kunne ikke hentes');
  if (!data) throw new Error('Filen findes ikke i cloud.');
  return data;
}

export async function removeAttachment(attachment: Attachment): Promise<void> {
  const guard = await authenticatedUser();
  // Remove the object first: failures leave its metadata visible so removal can be retried.
  const { error: objectError } = await client()
    .storage.from(attachmentBucket)
    .remove([objectPath(attachment)]);
  await assertSession(guard);
  fail(objectError, 'Filen kunne ikke slettes');
  const { data, error } = await client()
    .from('attachments')
    .delete()
    .eq('id', attachment.id)
    .eq('estate_id', attachment.estateId)
    .select('id');
  await assertSession(guard);
  fail(error, 'Filen er slettet, men metadata kunne ikke fjernes; prøv igen');
  if (!data?.length) throw new Error('Filens metadata findes ikke, eller du mangler adgang.');
  cloudAttachmentIds.delete(attachment.id);
}

// Profile saves do not replace an estate snapshot or require a prior estate revision.
export async function saveAccountProfile(
  state: AppState,
  profile: AppState['profile'],
): Promise<void> {
  const user = await authenticatedUser();
  assertStateOwner(state, user.id);
  if (!profile.name.trim() || profile.name.length > 120 || profile.company.length > 160)
    throw new Error('Udfyld dit navn og kontrollér længden af kontaktoplysningerne.');
  await assertSession(user);
  const result = await client().from('profiles').upsert({
    user_id: user.id,
    name: profile.name.trim(),
    company: profile.company.trim(),
    updated_at: new Date().toISOString(),
  });
  await assertSession(user);
  fail(result.error, 'Profilen er gemt lokalt, men kunne ikke gemmes i kontoen');
}

export interface AdminUser {
  user_id: string;
  email: string;
  name: string;
  deactivated?: boolean;
  isAdmin?: boolean;
  archived?: boolean;
  created_at?: string;
  last_sign_in_at?: string;
  isProfessional?: boolean;
  tier?: SubscriptionTier;
  notes?: string;
}

export interface AdminEstateMember {
  userId: string;
  email: string;
  role: 'owner' | 'advisor' | 'viewer';
}

export interface AdminEstate {
  estate_id: string;
  name: string;
  address: string;
  active: boolean;
  created_at: string;
  field_count: number;
  members: AdminEstateMember[];
  center?: [number, number];
  total_area_ha?: number;
  fields_with_seed?: number;
  seed_area_ha?: number;
  owner_id?: string;
  owner_name?: string;
  owner_email?: string;
}

export async function loadAccountAccess(userId?: string): Promise<{
  accountType: AccountType;
  subscriptionTier: SubscriptionTier;
}> {
  const session = await getSession();
  const targetUserId = userId ?? session?.user?.id;
  if (!targetUserId || targetUserId !== session?.user?.id) {
    return { accountType: 'standard', subscriptionTier: 'free' };
  }
  // En gemt session kan stadig indeholde det betalte niveau efter en opsigelse.
  const { data, error } = await client().rpc('get_account_access');
  fail(error, 'Abonnementsadgangen kunne ikke kontrolleres');
  const tier = data?.subscriptionTier;
  const accountType = data?.accountType;
  if (
    !['free', 'semipro', 'pro'].includes(tier) ||
    accountType !== (tier === 'pro' ? 'professional' : 'standard')
  ) {
    throw new Error('Serveren returnerede ugyldig abonnementsadgang.');
  }
  return { accountType, subscriptionTier: tier };
}

export async function loadAccountType(userId?: string): Promise<AccountType> {
  const { accountType } = await loadAccountAccess(userId);
  return accountType;
}

export async function loadSubscriptionTier(userId?: string): Promise<SubscriptionTier> {
  const { subscriptionTier } = await loadAccountAccess(userId);
  return subscriptionTier;
}

export async function lookupProfessionalMarkblok(lat: number, lng: number): Promise<Response> {
  const session = await getSession();
  if (!session?.access_token) throw new Error('Login kræves.');
  return fetch(`/api/professional/markblokke/lookup?lat=${lat}&lng=${lng}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
    signal: AbortSignal.timeout(15_000),
  });
}

export interface ProfessionalClient {
  client_user_id: string;
  email: string;
  name: string;
  estate_count: number;
  linked_at: string;
  status?: 'active' | 'pending';
  estate_id?: string;
  estate_name?: string;
  estate_address?: string;
}

export function normalizeEstateRow(row: any): Estate {
  return {
    id: String(row?.id || ''),
    name: String(row?.name || 'Mit Revir'),
    address: String(row?.address || ''),
    center: (Array.isArray(row?.center) &&
    row.center.length === 2 &&
    row.center.every((n: any) => typeof n === 'number' && Number.isFinite(n))
      ? [Number(row.center[0]), Number(row.center[1])]
      : [11.82, 55.4]) as [number, number],
    createdAt: String(row?.created_at || row?.createdAt || new Date().toISOString()),
    ownerUserId: row?.owner_user_id || row?.ownerUserId,
  };
}

export interface CreateClientEstateParams {
  clientId: string;
  name: string;
  address?: string;
  center?: [number, number];
}

export interface CreateClientEstateResult {
  ok: boolean;
  estate: Estate;
}

export async function professionalCreateClientEstate(
  params: CreateClientEstateParams,
): Promise<CreateClientEstateResult> {
  if (!params.clientId || typeof params.clientId !== 'string') {
    throw new Error('Klient-ID er påkrævet.');
  }
  if (!params.name || typeof params.name !== 'string' || !params.name.trim()) {
    throw new Error('Revirnavn er påkrævet.');
  }

  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');

  const safeCenter =
    Array.isArray(params.center) &&
    params.center.length === 2 &&
    params.center.every((n) => typeof n === 'number' && Number.isFinite(n))
      ? params.center
      : [11.82, 55.4];

  let response: Response | undefined;
  let data: any = {};
  let endpointAvailable = false;

  try {
    response = await fetch('/api/professional/estates', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId: params.clientId,
        name: params.name.trim(),
        address: params.address?.trim() || '',
        center: safeCenter,
      }),
      signal: AbortSignal.timeout(15000),
    });

    if (response) {
      endpointAvailable = response.status !== 404 && response.status !== 501;
      try {
        data = await response.json();
      } catch {
        // Response was not JSON
      }
    }
  } catch {
    endpointAvailable = false;
  }

  if (endpointAvailable && response) {
    await assertSession(guard);
    if (!response.ok) {
      throw new Error(data?.error || 'Reviret kunne ikke oprettes.');
    }
    if (data?.estate) {
      return { ok: true, estate: normalizeEstateRow(data.estate) };
    }
  }

  await assertSession(guard);
  const { data: rpcData, error: rpcError } = await client().rpc(
    'professional_create_client_estate',
    {
      target_client_id: params.clientId,
      estate_name: params.name.trim(),
      estate_address: params.address?.trim() || null,
      estate_center: safeCenter,
    },
  );
  await assertSession(guard);
  fail(rpcError, 'Reviret kunne ikke oprettes');

  const rawEstate = rpcData?.estate || rpcData;
  return { ok: true, estate: normalizeEstateRow(rawEstate) };
}

export async function professionalListClients(): Promise<ProfessionalClient[]> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  if (session?.access_token) {
    try {
      const res = await fetch('/api/professional/clients', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        signal: AbortSignal.timeout(8000),
      });
      if (res.ok) {
        const body = (await res.json()) as { clients?: ProfessionalClient[] };
        if (Array.isArray(body.clients)) {
          await assertSession(guard);
          return body.clients;
        }
      }
    } catch {
      // fallback to RPC
    }
  }
  await assertSession(guard);
  const { data, error } = await client().rpc('professional_list_clients');
  await assertSession(guard);
  fail(error, 'Kunder kunne ikke hentes');
  return (data ?? []) as ProfessionalClient[];
}

export interface ProfessionalInviteClientParams {
  email: string;
  name: string;
  estateName?: string;
  estateAddress?: string;
  estateCenter?: [number, number];
}

export interface ProfessionalInviteClientResult {
  id: string;
  inviteUrl?: string;
  message?: string;
  estateId?: string;
  estate?: Estate;
  existing?: boolean;
}

export async function professionalInviteClient(
  params: ProfessionalInviteClientParams,
): Promise<ProfessionalInviteClientResult> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const payload: Record<string, unknown> = {
    email: params.email.trim(),
    name: params.name.trim(),
  };
  if (params.estateName && params.estateName.trim()) {
    payload.estateName = params.estateName.trim();
  }
  if (params.estateAddress && params.estateAddress.trim()) {
    payload.estateAddress = params.estateAddress.trim();
  }
  if (
    Array.isArray(params.estateCenter) &&
    params.estateCenter.length === 2 &&
    params.estateCenter.every((n) => typeof n === 'number' && Number.isFinite(n))
  ) {
    payload.estateCenter = params.estateCenter;
  }
  const response = await fetch('/api/professional/clients', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(15000),
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // Response was not JSON
  }
  await assertSession(guard);
  if (!response.ok) {
    throw new Error(data?.error || 'Kunden kunne ikke oprettes.');
  }
  if (data?.estate) {
    data.estate = normalizeEstateRow(data.estate);
  }
  return data;
}

export async function professionalSendInviteLink(email: string): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/professional/clients/invite-link', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: email.trim() }),
    signal: AbortSignal.timeout(15000),
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // Response was not JSON
  }
  await assertSession(guard);
  if (!response.ok) {
    throw new Error(data?.error || 'Linket kunne ikke sendes.');
  }
}

export async function professionalUnlinkClient(clientId: string): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch(`/api/professional/clients/${encodeURIComponent(clientId)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
    signal: AbortSignal.timeout(15000),
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // Response was not JSON
  }
  await assertSession(guard);
  if (!response.ok) {
    throw new Error(data?.error || 'Kunden kunne ikke frakobles.');
  }
}

export async function professionalListPendingInvitations(): Promise<ProfessionalClient[]> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) return [];

  try {
    const res = await fetch('/api/professional/pending-invitations', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.pending) ? data.pending : [];
  } catch {
    return [];
  }
}

export async function professionalCancelInvitation(clientId: string): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) {
    throw new Error('Du skal være logget ind.');
  }

  const res = await fetch(`/api/professional/pending-invitations/${encodeURIComponent(clientId)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Invitationen kunne ikke annulleres.');
  }
}

export async function professionalAcceptInvitation(
  advisorId?: string,
): Promise<{ ok: boolean; advisorId?: string }> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/professional/accept-invitation', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ advisorId }),
    signal: AbortSignal.timeout(15000),
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // Response was not JSON
  }
  await assertSession(guard);
  if (!response.ok) {
    throw new Error(data?.error || 'Invitationen kunne ikke accepteres.');
  }
  return data;
}

export async function professionalRejectInvitation(): Promise<{ ok: boolean }> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/professional/reject-invitation', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(15000),
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // Response was not JSON
  }
  await assertSession(guard);
  if (!response.ok) {
    throw new Error(data?.error || 'Invitationen kunne ikke afvises.');
  }
  return data;
}

export async function adminListUsers(): Promise<AdminUser[]> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  let users: AdminUser[] = [];
  if (session?.access_token) {
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const body = (await res.json()) as { users?: AdminUser[] };
        if (Array.isArray(body.users)) {
          await assertSession(guard);
          users = body.users.filter((u) => !u.archived);
        }
      }
    } catch {
      // fallback to RPC
    }
  }
  if (users.length === 0) {
    await assertSession(guard);
    const { data, error } = await client().rpc('admin_list_users');
    await assertSession(guard);
    fail(error, 'Brugere kunne ikke hentes');
    users = (data ?? [])
      .filter((row: AdminUser) => !row.archived)
      .map((row: AdminUser & { is_admin?: boolean }) => ({
        ...row,
        isAdmin: row.is_admin === true,
      }));
  }
  try {
    const { data: profRows } = await client().from('professional_accounts').select('user_id');
    await assertSession(guard);
    if (Array.isArray(profRows)) {
      const profSet = new Set(profRows.map((r: { user_id: string }) => r.user_id));
      users = users.map((u) => {
        const isProfessional = profSet.has(u.user_id) || u.isProfessional === true;
        return {
          ...u,
          isProfessional,
          tier: u.tier || (isProfessional ? 'pro' : 'free'),
        };
      });
    } else {
      users = users.map((u) => ({
        ...u,
        tier: u.tier || (u.isProfessional ? 'pro' : 'free'),
      }));
    }
  } catch {
    // If professional_accounts cannot be queried, keep current users
    users = users.map((u) => ({
      ...u,
      tier: u.tier || (u.isProfessional ? 'pro' : 'free'),
    }));
  }
  return users;
}

export async function adminGrantProfessional(userId: string, company = ''): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/admin/users', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, isProfessional: true, company }),
  });
  const data = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(data.error || 'Klassificeringen kunne ikke tildeles.');
}

export async function adminRevokeProfessional(userId: string): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  // Revoking the professional classification must NOT delete the professional's own account
  // or their estates. It removes the professional classification and associated advisor links
  // to client estates, while leaving the user's account and personal estates completely untouched.
  const response = await fetch('/api/admin/users', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, isProfessional: false }),
  });
  const data = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(data.error || 'Klassificeringen kunne ikke fjernes.');
}

export async function adminSetUserDeactivated(userId: string, deactivated: boolean): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/admin/users', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, deactivated }),
  });
  const data = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(data.error || 'Brugerens status kunne ikke ændres.');
}

export interface AdminUpdateUserData {
  name?: string;
  email?: string;
  notes?: string;
  deactivated?: boolean;
  isProfessional?: boolean;
  company?: string;
  tier?: SubscriptionTier;
}

export async function adminUpdateUser(userId: string, data: AdminUpdateUserData): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/admin/users', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      ...(data.name !== undefined ? { name: data.name.trim() } : {}),
      ...(data.email !== undefined ? { email: data.email.trim() } : {}),
      ...(data.notes !== undefined ? { notes: data.notes } : {}),
      ...(data.deactivated !== undefined ? { deactivated: data.deactivated } : {}),
      ...(data.isProfessional !== undefined ? { isProfessional: data.isProfessional } : {}),
      ...(data.company !== undefined ? { company: data.company.trim() } : {}),
      ...(data.tier !== undefined ? { tier: data.tier } : {}),
    }),
    signal: AbortSignal.timeout(15000),
  });
  const resData = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(resData.error || 'Brugerens oplysninger kunne ikke gemmes.');
}

export async function adminUpdateCustomer(
  userId: string,
  details: { name?: string; email?: string; notes?: string; tier?: SubscriptionTier },
): Promise<void> {
  return adminUpdateUser(userId, details);
}

export async function adminDeleteUser(userId: string): Promise<void> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch(`/api/admin/users?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  });
  const data = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(data.error || 'Brugeren kunne ikke slettes.');
}

export async function adminDeleteEstate(estateId: string): Promise<void> {
  return deleteEstate(estateId);
}

export function normalizeAdminEstate(item: any): AdminEstate {
  const members: AdminEstateMember[] = Array.isArray(item.members) ? [...item.members] : [];

  const ownerId = (item.owner_id || item.owner_user_id || '') as string;
  const ownerEmail = (item.owner_email || '') as string;

  if (members.length === 0 && ownerId) {
    members.push({
      userId: ownerId,
      email: ownerEmail,
      role: 'owner',
    });
  }

  return {
    estate_id: String(item.estate_id || item.id || ''),
    name: String(item.name || ''),
    address: String(item.address || ''),
    active: Boolean(item.active ?? true),
    created_at: String(item.created_at || new Date().toISOString()),
    field_count: Number(item.field_count ?? 0),
    members,
    center:
      Array.isArray(item.center) && item.center.length === 2
        ? [Number(item.center[0]), Number(item.center[1])]
        : undefined,
    total_area_ha:
      typeof item.total_area_ha === 'number' ? item.total_area_ha : Number(item.total_area_ha ?? 0),
    fields_with_seed:
      typeof item.fields_with_seed === 'number'
        ? item.fields_with_seed
        : Number(item.fields_with_seed ?? 0),
    seed_area_ha:
      typeof item.seed_area_ha === 'number' ? item.seed_area_ha : Number(item.seed_area_ha ?? 0),
    owner_id: ownerId || undefined,
    owner_name: typeof item.owner_name === 'string' ? item.owner_name : undefined,
    owner_email: ownerEmail || undefined,
  };
}

export async function adminListEstatesSummary(): Promise<AdminEstate[]> {
  const guard = await authenticatedUser();
  const { data, error } = await client().rpc('admin_list_estates_summary');
  await assertSession(guard);
  fail(error, 'Revir-overblik kunne ikke hentes');
  return ((data ?? []) as Array<Partial<AdminEstate> & Record<string, unknown>>).map(
    normalizeAdminEstate,
  );
}

export async function adminListEstates(): Promise<AdminEstate[]> {
  const guard = await authenticatedUser();
  const { data, error } = await client().rpc('admin_list_estates');
  await assertSession(guard);
  fail(error, 'Revirer kunne ikke hentes');
  return ((data ?? []) as Array<Partial<AdminEstate> & Record<string, unknown>>).map(
    normalizeAdminEstate,
  );
}

export async function adminCreateEstate(
  ownerId: string,
  name: string,
  address = '',
  center?: [number, number],
): Promise<string> {
  const guard = await authenticatedUser();
  const safeCenter =
    Array.isArray(center) &&
    center.length === 2 &&
    center.every((n) => typeof n === 'number' && Number.isFinite(n))
      ? center
      : [11.82, 55.4];
  const { data, error } = await client().rpc('admin_create_estate', {
    owner_id: ownerId,
    estate_name: name.trim(),
    estate_address: address.trim(),
    estate_center: safeCenter,
  });
  await assertSession(guard);
  fail(error, 'Reviret kunne ikke oprettes');
  return data as string;
}

export async function adminAddMember(
  estateId: string,
  memberId: string,
  role: 'advisor' | 'viewer',
): Promise<void> {
  const guard = await authenticatedUser();
  const { error } = await client().rpc('admin_add_member', {
    estate_key: estateId,
    member_id: memberId,
    member_role: role,
  });
  await assertSession(guard);
  fail(error, 'Brugeren kunne ikke tilknyttes reviret');
}

export async function adminRemoveMember(estateId: string, memberId: string): Promise<void> {
  const guard = await authenticatedUser();
  const { error } = await client().rpc('admin_remove_member', {
    estate_key: estateId,
    member_id: memberId,
  });
  await assertSession(guard);
  fail(error, 'Brugerens tilknytning kunne ikke fjernes');
}

export async function adminSetEstateActive(estateId: string, active: boolean): Promise<void> {
  const guard = await authenticatedUser();
  const { error } = await client().rpc('admin_set_estate_active', {
    estate_key: estateId,
    is_active: active,
  });
  await assertSession(guard);
  fail(error, 'Revirets status kunne ikke ændres');
}

export async function adminCreateUserAccount(params: {
  email: string;
  password: string;
  name: string;
}): Promise<{ id: string; email: string }> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch('/api/admin/users', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  const data = await response.json();
  await assertSession(guard);
  if (!response.ok) throw new Error(data.error || 'Brugeren kunne ikke oprettes.');
  return data;
}

export interface VisitorEvent {
  timestamp: string;
  path: string;
  label?: string;
  details?: string;
}

export interface CustomerAuditProfile {
  userEmail: string;
  userId?: string;
  totalVisits: number;
  totalDurationSeconds: number;
  firstSeen: string;
  lastSeen: string;
  devices: ('desktop' | 'mobile' | 'tablet')[];
  pagesVisited: string[];
  recentEvents: VisitorEvent[];
  referrers: string[];
}

export interface VisitorStatsOverview {
  totalVisitors: number;
  visitorsToday: number;
  visitorsThisWeek: number;
  visitorsThisMonth: number;
  averageDurationSeconds?: number;
  deviceBreakdown: {
    mobile: number;
    desktop: number;
    tablet: number;
  };
  visitorTypes: {
    guests: number;
    registered: number;
  };
  popularPages?: Array<{ path: string; count: number }>;
  customerProfiles?: CustomerAuditProfile[];
  recentVisitors: {
    visitorId: string;
    firstSeen: string;
    lastSeen: string;
    visitCount: number;
    device: 'desktop' | 'mobile' | 'tablet';
    pagesVisited: string[];
    isGuest: boolean;
    userEmail?: string;
    totalDurationSeconds?: number;
    referrer?: string;
    recentEvents?: VisitorEvent[];
  }[];
  customerLogins?: {
    totalCustomers: number;
    activeToday: number;
    activeThisWeek: number;
    neverLoggedIn: number;
  };
}

export async function adminGetStatsOverview(): Promise<VisitorStatsOverview | null> {
  const guard = await authenticatedUser();
  const session = await getSession();
  await assertSession(guard);
  if (!session?.access_token) return null;
  try {
    const res = await fetch('/api/stats/overview', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (res.ok) {
      await assertSession(guard);
      return (await res.json()) as VisitorStatsOverview;
    }
  } catch {
    // fallback or offline
  }
  return null;
}

export interface RecordSiteVisitOptions {
  heartbeatSeconds?: number;
  action?: string;
  label?: string;
  details?: string;
  analyticsConsent?: boolean;
}

export const ADMIN_EMAILS_LOWER = [
  'mikkelmo2806@gmail.com',
  'emilbeiner@gmail.com',
  'mikkel@gmail.com',
  'mikkelmortensen12331@gmail.com',
  'plotwise.faunacare@gmail.com',
];

/**
 * Tjekker om en e-mail tilhører en SoMe demo/testkonto (f.eks. almindelig@plotwise.dk, semipro@plotwise.dk, professionel@plotwise.dk).
 * Disse profiler bruges til videooptagelse, skærmbilleder og SoMe-promovering.
 * Deres data (jord, revir, vildtagre, frøbehov) må ALDRIG tælle med i platformens regnskaber,
 * men profilerne skal forblive 100% funktionelle og tilgængelige for login og 1-klik rådgiver-hop.
 */
export function isSoMeDemoEmail(email?: string | null): boolean {
  if (!email) return false;
  const lower = email.trim().toLowerCase();
  if (lower === 'plotwise.faunacare@gmail.com') return false;
  return (
    lower.includes('@plotwise') ||
    lower.includes('plotwise.dk') ||
    (lower.includes('plotwise') && !lower.includes('gmail.com'))
  );
}

export function isSoMeDemoUser(user?: { email?: string | null } | null): boolean {
  return isSoMeDemoEmail(user?.email);
}

export function isSoMeDemoEstate(estate: AdminEstate, someUserIds?: Set<string>): boolean {
  if (estate.owner_email && isSoMeDemoEmail(estate.owner_email)) return true;
  if (estate.owner_id && someUserIds && someUserIds.has(estate.owner_id)) return true;
  if (Array.isArray(estate.members)) {
    if (
      estate.members.some(
        (m) => isSoMeDemoEmail(m.email) || (someUserIds && someUserIds.has(m.userId)),
      )
    ) {
      return true;
    }
  }
  return false;
}

export function isDeviceMarkedAdmin(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
  return localStorage.getItem('plotwise_is_admin') === '1';
}

export function setDeviceAdmin(isAdmin: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  if (isAdmin) {
    localStorage.setItem('plotwise_is_admin', '1');
  } else {
    localStorage.removeItem('plotwise_is_admin');
  }
}

export function recordSiteVisit(
  page: string,
  isGuest: boolean,
  userEmail?: string,
  userId?: string,
  options?: RecordSiteVisitOptions,
): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    // 1. Ekskluder hvis enheden er markeret som admin
    if (isDeviceMarkedAdmin()) return;

    // 2. Ekskluder hvis e-mail tilhører admin eller testbruger
    if (userEmail) {
      const email = userEmail.trim().toLowerCase();
      if (ADMIN_EMAILS_LOWER.includes(email)) {
        setDeviceAdmin(true);
        return;
      }
      if (
        email.endsWith('.test') ||
        email.endsWith('.invalid') ||
        email.startsWith('fixture@') ||
        email.startsWith('test@')
      ) {
        return;
      }
    }

    // 3. Ekskluder admin-sider
    if (
      [
        'superadmin',
        'admin-activity',
        'admin-email',
        'admin-traffic',
        'admin-stats',
        'admin-users',
      ].includes(page)
    ) {
      return;
    }

    // 4. Ekskluder automatiserede test-browsere og crawlere
    if (typeof navigator !== 'undefined') {
      if (
        navigator.webdriver ||
        /HeadlessChrome|Playwright|bot|crawl|spider/i.test(navigator.userAgent)
      ) {
        return;
      }
    }

    // 5. Ekskluder lokale udviklingsmiljøer og test-hosts
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host.endsWith('.test') ||
        host.endsWith('.invalid')
      ) {
        return;
      }
    }

    // 6. Lovpligtigt samtykketjek (Cookiebekendtgørelsen & GDPR)
    let hasConsent = false;
    if (!isGuest && options?.analyticsConsent !== undefined) {
      hasConsent = Boolean(options.analyticsConsent);
    } else {
      try {
        const rawConsent = localStorage.getItem('plotwise_consent');
        if (rawConsent) {
          const parsed = JSON.parse(rawConsent);
          if (parsed && typeof parsed.analytics === 'boolean') {
            hasConsent = parsed.analytics;
          }
        }
      } catch {
        hasConsent = false;
      }
    }

    if (!hasConsent) {
      return;
    }

    let visitorId = localStorage.getItem('plotwise_visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + crypto.randomUUID();
      localStorage.setItem('plotwise_visitor_id', visitorId);
    }

    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 1024;
    const device = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    void fetch('/api/stats/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorId,
        path: page,
        device,
        isGuest,
        userEmail,
        userId,
        referrer: document.referrer || '',
        heartbeatSeconds: options?.heartbeatSeconds,
        action: options?.action,
        label: options?.label,
        details: options?.details,
      }),
    }).catch(() => {});
  } catch {
    // Ignore storage/network errors
  }
}

export interface FaunamasterClickItem {
  name: string;
  quantity: number;
  unit?: string;
  priceDkk?: number;
}

export interface FaunamasterClickEvent {
  userId?: string;
  userEmail?: string;
  userName?: string;
  estateId?: string;
  estateName?: string;
  source: 'cart_checkout' | 'catalog_product' | 'shop_link';
  targetUrl: string;
  productName?: string;
  items?: FaunamasterClickItem[];
  estimatedValueDkk?: number;
}

export interface FaunamasterClickRecord extends FaunamasterClickEvent {
  id: string;
  created_at: string;
}

export interface FaunamasterClicksSummary {
  total_clicks: number;
  clicks_today: number;
  clicks_this_month: number;
  total_cart_value_dkk: number;
  recent_clicks: FaunamasterClickRecord[];
}

export async function recordFaunamasterClick(event: FaunamasterClickEvent): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const clickRecord = {
      user_id: event.userId || null,
      user_email: event.userEmail || null,
      user_name: event.userName || null,
      estate_id: event.estateId || null,
      estate_name: event.estateName || null,
      source: event.source,
      target_url: event.targetUrl,
      product_name: event.productName || null,
      items: event.items && event.items.length > 0 ? event.items : null,
      estimated_value_dkk: Number(event.estimatedValueDkk) || 0,
    };

    // 1. Send to Supabase
    try {
      const c = client();
      await c.from('faunamaster_clicks').insert(clickRecord);
    } catch {
      // Offline fallback: handled by localStorage below
    }

    // 2. Cache in localStorage for immediate updates and preview mode
    try {
      const existingRaw = localStorage.getItem('plotwise_faunamaster_clicks');
      const existing: FaunamasterClickRecord[] = existingRaw ? JSON.parse(existingRaw) : [];
      const newEntry: FaunamasterClickRecord = {
        id: 'local_' + crypto.randomUUID(),
        created_at: new Date().toISOString(),
        ...event,
      };
      const updated = [newEntry, ...existing].slice(0, 50);
      localStorage.setItem('plotwise_faunamaster_clicks', JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  } catch {
    // Ignore unexpected errors
  }
}

export async function adminGetFaunamasterClicksSummary(): Promise<FaunamasterClicksSummary | null> {
  const guard = await authenticatedUser();
  await assertSession(guard);
  try {
    const c = client();
    const { data, error } = await c.rpc('admin_get_faunamaster_clicks_summary');
    if (!error && Array.isArray(data) && data.length > 0) {
      const row = data[0];
      return {
        total_clicks: Number(row.total_clicks) || 0,
        clicks_today: Number(row.clicks_today) || 0,
        clicks_this_month: Number(row.clicks_this_month) || 0,
        total_cart_value_dkk: Number(row.total_cart_value_dkk) || 0,
        recent_clicks: Array.isArray(row.recent_clicks) ? row.recent_clicks : [],
      };
    }
  } catch {
    // Fallback if offline or preview
  }

  // Fallback to locally recorded clicks if any
  try {
    const existingRaw = localStorage.getItem('plotwise_faunamaster_clicks');
    if (existingRaw) {
      const list: FaunamasterClickRecord[] = JSON.parse(existingRaw);
      const todayStr = new Date().toISOString().slice(0, 10);
      const monthStr = new Date().toISOString().slice(0, 7);
      return {
        total_clicks: list.length,
        clicks_today: list.filter((c) => c.created_at?.startsWith(todayStr)).length,
        clicks_this_month: list.filter((c) => c.created_at?.startsWith(monthStr)).length,
        total_cart_value_dkk: list.reduce((sum, c) => sum + (c.estimatedValueDkk || 0), 0),
        recent_clicks: list,
      };
    }
  } catch {
    // ignore
  }

  return null;
}

export async function acceptUserTerms(analytics: boolean = false): Promise<void> {
  const { error } = await client().rpc('accept_user_terms', { analytics });
  if (error) throw new Error(error.message || 'Kunne ikke gemme brugersamtykke.');
}

export async function setMarketingConsent(consent: boolean): Promise<void> {
  const { error } = await client().rpc('set_marketing_consent', { consent });
  if (error) throw new Error(error.message || 'Kunne ikke gemme dit valg om påmindelser.');
}

export type MarketingStatus = 'ja' | 'nej' | 'afventer';

export interface MarketingContact {
  user_id: string;
  email: string;
  name: string;
  status: MarketingStatus;
  answered_at: string | null;
  field_count: number;
  fields_without_product: number;
}

export async function listMarketingContacts(): Promise<MarketingContact[]> {
  const { data, error } = await client().rpc('admin_marketing_overview');
  if (error) throw new Error(error.message || 'E-maillisten kunne ikke hentes.');
  return ((data ?? []) as MarketingContact[]).map((row) => ({
    ...row,
    field_count: Number(row.field_count),
    fields_without_product: Number(row.fields_without_product),
  }));
}

// ---------------------------------------------------------------------------------------------
// Abonnement (Stripe). Klienten vælger kun niveau og interval; pris, URL'er og adgang afgøres af
// serveren og Stripe-webhooken.
// ---------------------------------------------------------------------------------------------

export type BillingInterval = 'month' | 'year';

export interface BillingPlanPrice {
  amount: number | null;
  currency: string;
  priceId?: string;
  taxInclusive?: boolean;
}

export interface BillingStatus {
  enabled: boolean;
  subscription: {
    tier: SubscriptionTier;
    status: string;
    interval: BillingInterval | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  plans: Partial<Record<'semipro' | 'pro', Partial<Record<BillingInterval, BillingPlanPrice>>>>;
}

async function billingRequest<T>(path: string, method: 'GET' | 'POST', body?: unknown): Promise<T> {
  const session = await getSession();
  if (!session?.access_token) throw new Error('Login kræves.');
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(15000),
  });
  let data: { error?: string } = {};
  try {
    data = await response.json();
  } catch {
    // Svaret var ikke JSON
  }
  if (!response.ok) throw new Error(data?.error || 'Betalingen kunne ikke gennemføres.');
  return data as T;
}

export function loadBillingStatus(): Promise<BillingStatus> {
  return billingRequest<BillingStatus>('/api/billing/status', 'GET');
}

/** Returnerer Stripe Checkout-adressen. Kalderen sender brugeren derhen. */
export async function startBillingCheckout(
  tier: 'semipro' | 'pro',
  interval: BillingInterval,
  expectedPriceId?: string,
): Promise<string> {
  const { url } = await billingRequest<{ url: string }>('/api/billing/checkout', 'POST', {
    tier,
    interval,
    expectedPriceId,
  });
  return url;
}

export async function openBillingPortal(target?: {
  tier: 'semipro' | 'pro';
  interval: BillingInterval;
  expectedPriceId?: string;
}): Promise<string> {
  const { url } = await billingRequest<{ url: string }>(
    '/api/billing/portal',
    'POST',
    target ?? {},
  );
  return url;
}

/** Henter nyt token, så et lige betalt niveau (app_metadata) slår igennem uden nyt login. */
export async function refreshAccessAfterPayment(): Promise<void> {
  const { data, error } = await client().auth.refreshSession();
  fail(error, 'Abonnementsadgangen kunne ikke opdateres');
  if (!data.session) throw new Error('Log ind igen for at kontrollere dit abonnement.');
}

export function formatBillingPrice(price: BillingPlanPrice | undefined): string | null {
  if (!price || price.amount === null) return null;
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: price.amount % 100 === 0 ? 0 : 2,
  }).format(price.amount / 100);
}
